import type { ChannelsState } from '../types/composer'
import type { NoticeVariable } from '../types/variables'
import type { Localized } from '../types/notice'
import { resolveText } from './variables'

/** Everything the assistant knows about the notice being composed. */
export interface AiContext {
  title: string
  category: string
  categoryLabel: string
  audienceTotal: number
  audienceLabel: string
  channels: ChannelsState
  variables: NoticeVariable[]
  contentLang: 'fr' | 'en'
}

// --- TEXT HELPERS -----------------------------------------------------------

function toText(html: string, ctx: AiContext): string {
  return resolveText(html, ctx.variables, ctx.contentLang)
}

function htmlEscape(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function paragraphs(...lines: string[]): string {
  return lines
    .filter(Boolean)
    .map((line) => `<p>${htmlEscape(line)}</p>`)
    .join('')
}

/** Shortens to a hard limit without cutting a word in half. */
function clamp(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trim()}…`
}

/** The sentence of `text` that contains `keyword`, used to target a highlight. */
function sentenceWith(text: string, keyword: string): string | undefined {
  const sentences = text.split(/(?<=[.!?])\s+|\n+/)
  return sentences.find((s) => s.toLowerCase().includes(keyword.toLowerCase()))?.trim()
}

const TIME_PATTERN = /(\d{1,2}\s?h\b|\d{1,2}\s?:\s?\d{2}|\bmidi\b|\bminuit\b|\ba\.?m\.?\b|\bp\.?m\.?\b)/i
const DATE_PATTERN =
  /(\d{1,2}\s+(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre|january|february|march|april|may|june|july|august|september|october|november|december)|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2})/i
const LINK_PATTERN = /(https?:\/\/|windsor\.ca|<a\s)/i

// --- CHANNEL GENERATION -----------------------------------------------------

/** Openers that give the generated draft a tone matching the category. */
const OPENERS: Record<string, Localized> = {
  urgence: {
    fr: 'Message important de la Ville de Windsor.',
    en: 'Important message from the City of Windsor.',
  },
  eau: {
    fr: 'Avis concernant le réseau d’eau potable.',
    en: 'Notice regarding the drinking water network.',
  },
  travaux: {
    fr: 'Des travaux sont prévus dans votre secteur.',
    en: 'Work is planned in your area.',
  },
  collecte: {
    fr: 'Modification à l’horaire de collecte.',
    en: 'Change to the collection schedule.',
  },
  evenement: {
    fr: 'La Ville de Windsor vous convie à un événement.',
    en: 'The City of Windsor invites you to an event.',
  },
}

function opener(ctx: AiContext): string {
  const localized = OPENERS[ctx.category]
  if (localized) return localized[ctx.contentLang]
  return ctx.contentLang === 'fr'
    ? 'Information de la Ville de Windsor.'
    : 'Information from the City of Windsor.'
}

/** The best available one-line summary of what the notice is about. */
function coreMessage(ctx: AiContext): string {
  const candidates = [
    toText(ctx.channels.email.body, ctx),
    toText(ctx.channels.push.message, ctx),
    toText(ctx.channels.sms.text, ctx),
    toText(ctx.channels.social.postText, ctx),
    ctx.title,
  ]
  const found = candidates.find((c) => c.trim().length > 0) ?? ''
  return clamp(found.split(/\n+/)[0] ?? '', 180)
}

/** Generates a draft adapted to one channel from what the notice already holds. */
export function generateForChannel(
  key: keyof ChannelsState,
  ctx: AiContext,
): Partial<ChannelsState[keyof ChannelsState]> {
  const fr = ctx.contentLang === 'fr'
  const title = ctx.title.trim() || ctx.categoryLabel
  const core = coreMessage(ctx)
  const summary = core && core !== title ? core : ''
  const link = 'windsor.ca/avis'

  switch (key) {
    case 'email':
      return {
        subject: title,
        body: paragraphs(
          fr ? 'Chers résidents,' : 'Dear residents,',
          opener(ctx),
          summary || title,
          fr
            ? `Pour tous les détails et les mises à jour, consultez ${link}.`
            : `For full details and updates, visit ${link}.`,
          fr ? 'Merci de votre collaboration.' : 'Thank you for your cooperation.',
          fr ? 'Ville de Windsor' : 'City of Windsor',
        ),
      }

    case 'sms':
      return {
        text: `<p>${htmlEscape(
          clamp(
            `${title}. ${summary} ${fr ? 'Infos :' : 'Info:'} ${link}`.replace(
              /\s+/g,
              ' ',
            ),
            158,
          ),
        )}</p>`,
      }

    case 'push':
      return {
        title: `<p>${htmlEscape(clamp(title, 48))}</p>`,
        message: `<p>${htmlEscape(clamp(summary || opener(ctx), 98))}</p>`,
      }

    case 'voice':
      return {
        script: `<p>${htmlEscape(
          fr
            ? `Bonjour, ceci est un message automatisé de la Ville de Windsor. ${opener(
                ctx,
              )} ${title}. ${summary} Pour plus d'information, consultez le site ${link}. Merci.`
            : `Hello, this is an automated message from the City of Windsor. ${opener(
                ctx,
              )} ${title}. ${summary} For more information, visit ${link}. Thank you.`,
        )}</p>`,
      }

    case 'website':
      return {
        bannerText: `<p>${htmlEscape(title)}</p>`,
        ctaLabel: `<p>${htmlEscape(fr ? 'En savoir plus' : 'Learn more')}</p>`,
      }

    case 'social':
      return {
        postText: paragraphs(
          `${fr ? '📢' : '📢'} ${title}`,
          summary || opener(ctx),
          fr ? `Détails : ${link}` : `Details: ${link}`,
          '#Windsor #InfoCitoyen',
        ),
      }

    case 'screens':
      return {
        headline: `<p>${htmlEscape(clamp(title, 46))}</p>`,
        bodyText: `<p>${htmlEscape(
          clamp(
            `${summary || opener(ctx)} ${fr ? 'Détails :' : 'Details:'} ${link}`.replace(
              /\s+/g,
              ' ',
            ),
            178,
          ),
        )}</p>`,
      }

    case 'app':
      return {
        title: `<p>${htmlEscape(title)}</p>`,
        summary: `<p>${htmlEscape(
          clamp((summary || opener(ctx)).replace(/\s+/g, ' ').trim(), 140),
        )}</p>`,
        body: paragraphs(
          summary || opener(ctx),
          fr ? `Pour plus de détails : ${link}` : `For more details: ${link}`,
        ),
      }

    default:
      return {}
  }
}

/** True when a channel has nothing written in it yet. */
export function isChannelEmpty(
  key: keyof ChannelsState,
  channels: ChannelsState,
): boolean {
  const plain = (html: string) => {
    const el = document.createElement('div')
    el.innerHTML = html ?? ''
    return (el.textContent ?? '').trim().length > 0 || /<img\b/i.test(html ?? '')
  }
  switch (key) {
    case 'email':
      return !plain(channels.email.subject) && !plain(channels.email.body)
    case 'sms':
      return !plain(channels.sms.text)
    case 'push':
      return !plain(channels.push.title) && !plain(channels.push.message)
    case 'voice':
      return !plain(channels.voice.script)
    case 'website':
      return !plain(channels.website.bannerText)
    case 'social':
      return !plain(channels.social.postText)
    case 'screens':
      return !plain(channels.screens.headline) && !plain(channels.screens.bodyText)
    case 'app':
      return (
        !plain(channels.app.title) &&
        !plain(channels.app.summary) &&
        !plain(channels.app.body)
      )
    default:
      return true
  }
}

// --- CONTEXTUAL TIPS --------------------------------------------------------

export interface AiTip {
  id: string
  level: 'info' | 'warning'
  message: Localized
  /** Text excerpt to highlight in the editor when the tip is clicked. */
  highlight?: string
}

/** Recommendations shown under a channel's content. Never blocking. */
export function getChannelTips(
  key: keyof ChannelsState,
  ctx: AiContext,
): AiTip[] {
  const tips: AiTip[] = []
  const c = ctx.channels
  const fr = ctx.contentLang === 'fr'

  const add = (
    id: string,
    level: AiTip['level'],
    message: Localized,
    highlight?: string,
  ) => tips.push({ id: `${key}.${id}`, level, message, highlight })

  const closureWords = fr
    ? ['fermeture', 'fermée', 'entrave', 'interruption', 'coupure']
    : ['closure', 'closed', 'interruption', 'shutdown']

  const body =
    key === 'email'
      ? `${toText(c.email.subject, ctx)}\n${toText(c.email.body, ctx)}`
      : key === 'sms'
        ? toText(c.sms.text, ctx)
        : key === 'push'
          ? `${toText(c.push.title, ctx)}\n${toText(c.push.message, ctx)}`
          : key === 'voice'
            ? toText(c.voice.script, ctx)
            : key === 'website'
              ? `${toText(c.website.bannerText, ctx)}\n${toText(c.website.ctaLabel, ctx)}`
              : key === 'screens'
                ? `${toText(c.screens.headline, ctx)}\n${toText(c.screens.bodyText, ctx)}`
                : key === 'app'
                  ? `${toText(c.app.title, ctx)}\n${toText(c.app.summary, ctx)}\n${toText(c.app.body, ctx)}`
                  : toText(c.social.postText, ctx)

  if (!body.trim()) return tips

  // Missing reopening time on an interruption-type message.
  const closureWord = closureWords.find((w) => body.toLowerCase().includes(w))
  if (closureWord && !TIME_PATTERN.test(body)) {
    add(
      'reopening',
      'warning',
      {
        fr: 'Vous indiquez une fermeture mais aucune heure de réouverture.',
        en: 'You mention a closure but no reopening time.',
      },
      sentenceWith(body, closureWord),
    )
  }

  // No date at all on a time-sensitive category.
  if (
    ['travaux', 'eau', 'collecte', 'evenement', 'urgence'].includes(ctx.category) &&
    !DATE_PATTERN.test(body)
  ) {
    add('date', 'warning', {
      fr: 'Aucune date n’est mentionnée — les citoyens ne sauront pas quand cela s’applique.',
      en: 'No date is mentioned — citizens will not know when this applies.',
    })
  }

  switch (key) {
    case 'email': {
      if (!LINK_PATTERN.test(c.email.body)) {
        add('link', 'info', {
          fr: 'Ajoutez un lien vers la page de l’avis pour rediriger les questions.',
          en: 'Add a link to the notice page to deflect questions.',
        })
      }
      const subject = toText(c.email.subject, ctx)
      if (subject.length > 60) {
        add(
          'subject',
          'info',
          {
            fr: 'Objet un peu long : il sera tronqué sur mobile au-delà de 60 caractères.',
            en: 'Subject is long: it will be truncated on mobile past 60 characters.',
          },
          subject,
        )
      }
      break
    }
    case 'sms': {
      const text = toText(c.sms.text, ctx)
      if (text.length > 160) {
        add(
          'length',
          'warning',
          {
            fr: `Message de ${text.length} caractères : il sera facturé en deux SMS.`,
            en: `Message is ${text.length} characters: it will be billed as two texts.`,
          },
          text.slice(150),
        )
      }
      if (!LINK_PATTERN.test(text)) {
        add('link', 'info', {
          fr: 'Un lien court permettrait aux citoyens d’obtenir les détails.',
          en: 'A short link would let citizens get the full details.',
        })
      }
      break
    }
    case 'push': {
      const pushTitle = toText(c.push.title, ctx)
      if (pushTitle.length > 50) {
        add(
          'title',
          'warning',
          {
            fr: 'Titre tronqué sur l’écran verrouillé au-delà de 50 caractères.',
            en: 'Title is cut off on the lock screen past 50 characters.',
          },
          pushTitle,
        )
      }
      if (!toText(c.push.message, ctx).trim()) {
        add('message', 'info', {
          fr: 'Un message accompagnant le titre double le taux d’ouverture.',
          en: 'A message alongside the title doubles the open rate.',
        })
      }
      break
    }
    case 'voice': {
      const script = toText(c.voice.script, ctx)
      if (script.length > 420) {
        add(
          'length',
          'warning',
          {
            fr: 'Script long : au-delà de 40 secondes, la majorité des appels sont raccrochés.',
            en: 'Long script: past 40 seconds, most calls are hung up.',
          },
          script.slice(400, 460),
        )
      }
      if (!/windsor\.ca|\d{3}[\s.-]?\d{3}[\s.-]?\d{4}/.test(script)) {
        add('contact', 'info', {
          fr: 'Terminez par un numéro ou une adresse web — l’auditeur ne peut rien cliquer.',
          en: 'End with a phone number or web address — the listener cannot click anything.',
        })
      }
      break
    }
    case 'website': {
      if (!toText(c.website.ctaLabel, ctx).trim()) {
        add('cta', 'info', {
          fr: 'Sans libellé de bouton, la bannière n’est pas cliquable.',
          en: 'Without a button label, the banner is not clickable.',
        })
      }
      break
    }
    case 'social': {
      const post = toText(c.social.postText, ctx)
      if (!post.includes('#')) {
        add('hashtag', 'info', {
          fr: 'Ajoutez un mot-clic pour élargir la portée de la publication.',
          en: 'Add a hashtag to widen the post’s reach.',
        })
      }
      if (!/<img\b/i.test(c.social.postText)) {
        add('image', 'info', {
          fr: 'Une publication avec image obtient en moyenne deux fois plus d’interactions.',
          en: 'A post with an image gets on average twice the engagement.',
        })
      }
      break
    }
    case 'screens': {
      const headline = toText(c.screens.headline, ctx)
      if (headline.split(/\s+/).filter(Boolean).length > 8) {
        add(
          'headline',
          'warning',
          {
            fr: 'Titre trop long pour être lu de loin — visez moins de 8 mots.',
            en: 'Headline too long to read at a distance — aim for fewer than 8 words.',
          },
          headline,
        )
      }
      if (c.screens.locations.length === 0) {
        add('locations', 'warning', {
          fr: 'Aucun écran sélectionné — le message ne sera affiché nulle part.',
          en: 'No screen selected — the message will not be displayed anywhere.',
        })
      }
      if (LINK_PATTERN.test(toText(c.screens.bodyText, ctx))) {
        add('link', 'info', {
          fr: 'Un lien n’est pas cliquable sur un écran : privilégiez une adresse courte.',
          en: 'A link is not clickable on a screen: prefer a short address.',
        })
      }
      break
    }
    case 'app': {
      const appTitle = toText(c.app.title, ctx)
      if (appTitle.length > 70) {
        add(
          'title',
          'info',
          {
            fr: 'Titre long : il sera coupé sur deux lignes dans le fil de l’application.',
            en: 'Long title: it will wrap onto two lines in the app feed.',
          },
          appTitle,
        )
      }
      const appSummary = toText(c.app.summary, ctx)
      if (!appSummary.trim()) {
        add('summary', 'info', {
          fr: 'Sans résumé, le fil affichera le début du contenu, souvent coupé en pleine phrase.',
          en: 'Without a summary, the feed shows the start of the content, often cut mid-sentence.',
        })
      } else if (appSummary.length > 160) {
        add(
          'summary-length',
          'info',
          {
            fr: 'Résumé long : au-delà de 160 caractères, il sera tronqué sur la carte du fil.',
            en: 'Long summary: past 160 characters, it is truncated on the feed card.',
          },
          appSummary.slice(150),
        )
      }
      if (!toText(c.app.body, ctx).trim()) {
        add('body', 'info', {
          fr: 'Ajoutez un texte de détail : c’est la page que le citoyen ouvre depuis le fil.',
          en: 'Add a detail text: it is the page citizens open from the feed.',
        })
      }
      if (ctx.category === 'urgence' && !c.app.pinned) {
        add('pinned', 'info', {
          fr: 'Avis urgent : épinglez-le pour qu’il reste en tête du fil.',
          en: 'Urgent notice: pin it so it stays at the top of the feed.',
        })
      }
      break
    }
  }

  return tips
}

// --- GLOBAL AUDIT -----------------------------------------------------------

export type AuditGroup =
  | 'coherence'
  | 'missing'
  | 'audience'
  | 'variables'
  | 'languages'

export interface AuditFinding {
  id: string
  group: AuditGroup
  level: 'ok' | 'info' | 'warning'
  message: Localized
}

export const AUDIT_GROUP_LABELS: Record<AuditGroup, Localized> = {
  coherence: { fr: 'Cohérence entre les canaux', en: 'Consistency across channels' },
  missing: { fr: 'Informations manquantes', en: 'Missing information' },
  audience: { fr: 'Audience', en: 'Audience' },
  variables: { fr: 'Variables', en: 'Variables' },
  languages: { fr: 'Langues', en: 'Languages' },
}

const CHANNEL_NAMES: Record<keyof ChannelsState, Localized> = {
  email: { fr: 'Courriel', en: 'Email' },
  sms: { fr: 'SMS', en: 'SMS' },
  voice: { fr: 'Voix', en: 'Voice' },
  push: { fr: 'Push', en: 'Push' },
  website: { fr: 'Site web', en: 'Website' },
  social: { fr: 'Réseaux sociaux', en: 'Social media' },
  screens: { fr: 'Écrans publics', en: 'Public screens' },
  app: { fr: 'Application', en: 'App' },
}

/** A full pass over the notice. Purely advisory — it never blocks sending. */
export function auditNotice(ctx: AiContext): AuditFinding[] {
  const findings: AuditFinding[] = []
  const c = ctx.channels
  const activeKeys = (Object.keys(c) as (keyof ChannelsState)[]).filter(
    (k) => c[k].active,
  )

  const push = (
    id: string,
    group: AuditGroup,
    level: AuditFinding['level'],
    message: Localized,
  ) => findings.push({ id, group, level, message })

  // --- Coherence
  const emptyActive = activeKeys.filter((k) => isChannelEmpty(k, c))
  if (emptyActive.length > 0) {
    push('coherence.empty', 'coherence', 'warning', {
      fr: `${emptyActive
        .map((k) => CHANNEL_NAMES[k].fr)
        .join(', ')} : canal activé mais sans contenu.`,
      en: `${emptyActive
        .map((k) => CHANNEL_NAMES[k].en)
        .join(', ')}: channel switched on but empty.`,
    })
  }

  const bodies = activeKeys
    .filter((k) => !isChannelEmpty(k, c))
    .map((k) => ({
      key: k,
      text: toText(
        k === 'email'
          ? c.email.body
          : k === 'sms'
            ? c.sms.text
            : k === 'push'
              ? c.push.message
              : k === 'voice'
                ? c.voice.script
                : k === 'website'
                  ? c.website.bannerText
                  : c.social.postText,
        ctx,
      ),
    }))

  const withDate = bodies.filter((b) => DATE_PATTERN.test(b.text))
  if (withDate.length > 0 && withDate.length < bodies.length) {
    push('coherence.date', 'coherence', 'warning', {
      fr: `La date n’apparaît que sur ${withDate
        .map((b) => CHANNEL_NAMES[b.key].fr)
        .join(', ')} — les autres canaux l’omettent.`,
      en: `The date only appears on ${withDate
        .map((b) => CHANNEL_NAMES[b.key].en)
        .join(', ')} — the other channels omit it.`,
    })
  }

  if (activeKeys.length > 0 && emptyActive.length === 0 && bodies.length > 1) {
    push('coherence.ok', 'coherence', 'ok', {
      fr: `${activeKeys.length} canaux portent le même message.`,
      en: `${activeKeys.length} channels carry the same message.`,
    })
  }

  // --- Missing information
  if (!ctx.title.trim()) {
    push('missing.title', 'missing', 'warning', {
      fr: 'L’avis n’a pas de titre — il sert de repère dans la liste et les archives.',
      en: 'The notice has no title — it anchors the list and the archives.',
    })
  }
  if (!ctx.category) {
    push('missing.category', 'missing', 'warning', {
      fr: 'Aucune catégorie sélectionnée : l’avis ne sera pas classé sur le portail.',
      en: 'No category selected: the notice will not be filed on the portal.',
    })
  }
  if (activeKeys.length === 0) {
    push('missing.channels', 'missing', 'warning', {
      fr: 'Aucun canal de diffusion n’est activé.',
      en: 'No distribution channel is switched on.',
    })
  }
  const allText = bodies.map((b) => b.text).join(' ')
  if (allText && !DATE_PATTERN.test(allText)) {
    push('missing.date', 'missing', 'warning', {
      fr: 'Aucune date n’est mentionnée dans l’avis.',
      en: 'No date is mentioned anywhere in the notice.',
    })
  }
  if (allText && !TIME_PATTERN.test(allText)) {
    push('missing.time', 'missing', 'info', {
      fr: 'Aucune heure n’est précisée — utile pour un début ou une fin d’intervention.',
      en: 'No time is given — useful for the start or end of an intervention.',
    })
  }
  if (allText && !LINK_PATTERN.test(allText)) {
    push('missing.link', 'missing', 'info', {
      fr: 'Aucun lien ni contact : les citoyens appelleront la ville pour les détails.',
      en: 'No link or contact: citizens will call the city for details.',
    })
  }

  // --- Audience
  if (ctx.audienceTotal === 0) {
    push('audience.empty', 'audience', 'warning', {
      fr: 'L’audience sélectionnée ne contient aucun citoyen.',
      en: 'The selected audience contains no citizens.',
    })
  } else {
    push('audience.size', 'audience', 'ok', {
      fr: `${ctx.audienceLabel} — ${ctx.audienceTotal.toLocaleString('fr-CA')} citoyens ciblés.`,
      en: `${ctx.audienceLabel} — ${ctx.audienceTotal.toLocaleString('en-CA')} citizens targeted.`,
    })
  }
  if (ctx.category === 'urgence' && !c.sms.active && !c.push.active) {
    push('audience.urgent', 'audience', 'warning', {
      fr: 'Avis d’urgence sans SMS ni Push : les canaux les plus rapides sont inactifs.',
      en: 'Emergency notice without SMS or Push: the fastest channels are switched off.',
    })
  }

  // --- Variables
  const used = ctx.variables.filter((v) => {
    const haystack = Object.values(c)
      .map((channel) => Object.values(channel).join(' '))
      .join(' ')
    return haystack.includes(`data-var="${v.id}"`)
  })
  const missingValue = used.filter(
    (v) => v.kind === 'custom' && !v.value[ctx.contentLang].trim(),
  )
  if (missingValue.length > 0) {
    push('variables.missing', 'variables', 'warning', {
      fr: `${missingValue
        .map((v) => v.label.fr)
        .join(', ')} : variable utilisée sans valeur.`,
      en: `${missingValue
        .map((v) => v.label.en)
        .join(', ')}: variable used without a value.`,
    })
  } else if (used.length > 0) {
    push('variables.ok', 'variables', 'ok', {
      fr: `${used.length} variable(s) utilisée(s), toutes renseignées.`,
      en: `${used.length} variable(s) in use, all filled in.`,
    })
  } else {
    push('variables.none', 'variables', 'info', {
      fr: 'Aucune variable utilisée — le prénom du citoyen améliore l’ouverture de 12 %.',
      en: 'No variable in use — the citizen’s first name lifts opens by 12%.',
    })
  }

  // --- Languages
  const missingOtherLang = ctx.variables.filter((v) => {
    if (v.kind !== 'custom') return false
    const other = ctx.contentLang === 'fr' ? 'en' : 'fr'
    return v.value[ctx.contentLang].trim() && !v.value[other].trim()
  })
  if (missingOtherLang.length > 0) {
    push('languages.variables', 'languages', 'warning', {
      fr: `${missingOtherLang
        .map((v) => v.label.fr)
        .join(', ')} : aucune valeur en anglais.`,
      en: `${missingOtherLang
        .map((v) => v.label.en)
        .join(', ')}: no French value.`,
    })
  }
  push('languages.content', 'languages', 'info', {
    fr: 'Vérifiez la version anglaise via l’onglet de langue au-dessus du formulaire.',
    en: 'Check the French version through the language tab above the form.',
  })

  return findings
}
